// frontend/src/features/landingPages/components/LpCustomSelect.js
import React, { useState, useRef, useEffect } from 'react';

/**
 * Custom Dropdown Select for Landing Page Builder
 *
 * Renders a modern Lead Huntrix styled dropdown menu with icons, subtitles,
 * and checkmarks, while keeping a hidden native <select> in sync for complete
 * accessibility and test compatibility.
 */
export default function LpCustomSelect({
  id,
  value,
  options = [],
  onChange,
  disabled = false,
  className = '',
  placeholder = 'Select option...',
  defaultIcon = 'fas fa-user',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const selectRef = useRef(null);

  const selectedOption =
    options.find((opt) => String(opt.value) === String(value)) ||
    options.find((opt) => opt.value === '') ||
    options[0] ||
    null;

  // Close on click outside or Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleSelectOption = (optVal) => {
    if (disabled) return;
    setIsOpen(false);
    if (onChange) {
      onChange(optVal);
    }
    if (selectRef.current) {
      const nativeSetter = Object.getOwnPropertyDescriptor(
        window.HTMLSelectElement.prototype,
        'value'
      )?.set;
      if (nativeSetter) {
        nativeSetter.call(selectRef.current, optVal);
      } else {
        selectRef.current.value = optVal;
      }
      selectRef.current.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  return (
    <div className={`lp-custom-select-wrapper ${className}`} ref={containerRef}>
      {/* Synchronized hidden native select for full form/test compatibility */}
      <select
        id={id}
        ref={selectRef}
        className="lp-native-select-hidden"
        value={value || ''}
        disabled={disabled}
        onChange={(e) => {
          if (onChange) onChange(e.target.value);
        }}
        tabIndex={-1}
        aria-hidden="true"
      >
        {options.map((opt) => (
          <option key={String(opt.value)} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {/* Custom styled trigger button */}
      <button
        type="button"
        className={`lp-custom-select-trigger ${isOpen ? 'is-open' : ''}`}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <div className="lp-custom-select-left">
          <span className="lp-custom-select-lead-icon" aria-hidden="true">
            <i className={selectedOption?.icon || defaultIcon}></i>
          </span>
          <span className="lp-custom-select-value">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <span className={`lp-custom-select-chevron ${isOpen ? 'open' : ''}`} aria-hidden="true">
          <i className="fas fa-chevron-down"></i>
        </span>
      </button>

      {/* Custom Dropdown Menu */}
      {isOpen && (
        <div className="lp-custom-select-menu" role="listbox">
          <div className="lp-custom-select-scroll">
            {options.map((opt) => {
              const isSelected = String(opt.value) === String(value || '');
              return (
                <div
                  key={String(opt.value)}
                  className={`lp-custom-select-option ${isSelected ? 'is-selected' : ''}`}
                  onClick={() => handleSelectOption(opt.value)}
                  role="option"
                  aria-selected={isSelected}
                >
                  <span className="lp-custom-option-icon" aria-hidden="true">
                    <i className={opt.icon || defaultIcon}></i>
                  </span>
                  <div className="lp-custom-option-text">
                    <span className="lp-custom-option-label">{opt.label}</span>
                    {opt.subtitle && <span className="lp-custom-option-sub">{opt.subtitle}</span>}
                  </div>
                  {isSelected && (
                    <span className="lp-custom-option-check" aria-hidden="true">
                      <i className="fas fa-check"></i>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
